/**
 * =============================================================================
 * SearchProvider.tsx — Provider globale per la ricerca contenuti.
 * =============================================================================
 *
 * Espone un contesto React che permette a qualunque componente di aprire il
 * dialog di ricerca preconfigurando l'ambito (sito / corso / modulo / sezione)
 * e l'eventuale id del contenitore di partenza.
 *
 * Gestisce inoltre la scorciatoia da tastiera Cmd/Ctrl+K, che apre la ricerca
 * con scope "site" (o con lo scope corrente se già aperta).
 * =============================================================================
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SearchDialog } from "./SearchDialog";

/** Ambiti di ricerca supportati. */
export type SearchScope = "site" | "course" | "module" | "section";

/** Stato interno del dialog di ricerca. */
export interface SearchOpenOptions {
  scope?: SearchScope;
  scopeId?: string | null;
  /** Etichetta umana del contesto (es. titolo del corso/modulo/sezione). */
  scopeLabel?: string | null;
}

interface SearchContextValue {
  open: (opts?: SearchOpenOptions) => void;
  close: () => void;
  isOpen: boolean;
  scope: SearchScope;
  scopeId: string | null;
  scopeLabel: string | null;
  setScope: (scope: SearchScope, id?: string | null, label?: string | null) => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

/** Hook per accedere al provider di ricerca. */
export function useSearch(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch deve essere usato all'interno di <SearchProvider>");
  return ctx;
}

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const [scope, setScopeState] = useState<SearchScope>("site");
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [scopeLabel, setScopeLabel] = useState<string | null>(null);

  const open = useCallback((opts?: SearchOpenOptions) => {
    if (opts?.scope) setScopeState(opts.scope);
    if (opts?.scopeId !== undefined) setScopeId(opts.scopeId ?? null);
    if (opts?.scopeLabel !== undefined) setScopeLabel(opts.scopeLabel ?? null);
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const setScope = useCallback((s: SearchScope, id?: string | null, label?: string | null) => {
    setScopeState(s);
    setScopeId(id ?? null);
    setScopeLabel(label ?? null);
  }, []);

  // Scorciatoia globale Cmd/Ctrl+K.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const value = useMemo<SearchContextValue>(
    () => ({ open, close, isOpen, scope, scopeId, scopeLabel, setScope }),
    [open, close, isOpen, scope, scopeId, scopeLabel, setScope]
  );

  return (
    <SearchContext.Provider value={value}>
      {children}
      <SearchDialog />
    </SearchContext.Provider>
  );
}